if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        if (this.loadRequest) {
          const request = this.loadRequest;
          this.finishLoading(request);
          request.controller.abort();
        }
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        if (this.loadRequest) return;
        const request = { controller: new AbortController(), opener };
        this.loadRequest = request;
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'), { signal: request.controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Quick add request failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (this.loadRequest !== request || !this.isConnected) return;
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Quick add product-info missing');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .catch((error) => {
            if (error.name === 'AbortError' || this.loadRequest !== request || !this.isConnected) return;
            this.modalContent.textContent = '';
            const fallback = document.createElement('div');
            fallback.className = 'quick-add-modal__content-info';
            const message = document.createElement('p');
            message.setAttribute('role', 'alert');
            const errorMessage = this.dataset.av5ErrorMessage || '';
            message.textContent = errorMessage && !errorMessage.startsWith('Translation missing')
              ? errorMessage
              : 'This piece could not be loaded here.';
            const link = document.createElement('a');
            link.className = 'link';
            link.href = opener.getAttribute('data-product-url') || '/collections/all';
            const viewDetails = this.dataset.av5ViewDetails || '';
            link.textContent = viewDetails && !viewDetails.startsWith('Translation missing')
              ? viewDetails
              : 'View full details';
            fallback.append(message, link);
            this.modalContent.append(fallback);
            super.show(opener);
          })
          .finally(() => {
            this.finishLoading(request);
          });
      }

      finishLoading(request) {
        if (this.loadRequest !== request) return;
        this.loadRequest = null;
        request.opener.removeAttribute('aria-disabled');
        request.opener.classList.remove('loading');
        request.opener.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.normalizeHeading(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        /*
         * APEX V5 Quick Inspect keeps Shopify's authoritative purchase surface
         * (media, price, options, quantity rules, selling plans and buy
         * buttons) and removes full-PDP/editorial utilities.  The data
         * attribute is the explicit V5 contract; quick-add-hidden is Dawn's
         * existing contract and is removed as a second, route-independent
         * guard.  Doing this in the core preprocessor means Quick Inspect is
         * correct even when the discovery route bundle is not present.
         */
        productElement
          .querySelectorAll('[data-av5-quick-add-hidden], .quick-add-hidden')
          .forEach((element) => element.remove());

        productElement
          .querySelectorAll('script[type="application/ld+json"]')
          .forEach((element) => element.remove());
      }

      normalizeHeading(productElement) {
        const heading = productElement.querySelector('.product__title h1');
        if (!heading) return;
        const link = document.createElement('a');
        const replacement = document.createElement('h2');
        link.className = 'product__title';
        link.href = productElement.dataset.url || '#';
        replacement.className = 'h1';
        replacement.innerHTML = heading.innerHTML;
        link.append(replacement);
        heading.replaceWith(link);
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        // The fetched PDP uses its desktop page grid. Restore the original
        // modal declaration before applying the existing Quick Inspect rules.
        productElement.querySelectorAll('[data-ancona-quick-add-sizes]').forEach((container) => {
          container.querySelectorAll('img').forEach((img) => {
            img.setAttribute('sizes', container.dataset.anconaQuickAddSizes);
          });
          container.removeAttribute('data-ancona-quick-add-sizes');
        });
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
